import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { User } from '@xver/@types/user';
import { Endpoints } from '@xver/apis';
import { Button, InputField } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { OtpInputField } from '@xver/components/Molecules';
import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { ME_QUERY_KEY } from '@xver/hooks/useMeQuery';
import { getErrorMessage } from '@xver/utils/error';
import { requestAuthUser } from '@xver/store/auth/auth.actions';
import storage from '@xver/utils/storage';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ChangeEmailSectionProps {
    user: User;
}

export default function ChangeEmailSection({ user }: ChangeEmailSectionProps) {
    const { t } = useTranslation('security');
    const queryClient = useQueryClient();
    const dispatch = useAppDispatch();

    const [stage, setStage] = useState<'idle' | 'enter-otp'>('idle');
    const [newEmail, setNewEmail] = useState('');
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [otpResetKey, setOtpResetKey] = useState(0);

    const requestValidation = useMemo(() => {
        const e: { newEmail?: string; password?: string } = {};
        if (!newEmail || !EMAIL_REGEX.test(newEmail)) e.newEmail = t('errors.emailInvalid');
        else if (newEmail === user.email) e.newEmail = t('errors.emailSame');
        if (!password) e.password = t('errors.passwordRequired');
        return e;
    }, [newEmail, password, user.email, t]);

    const requestMutation = useMutation({
        mutationFn: () => {
            const token = storage.get<string>('_zugang', '');
            if (!token) throw new Error(t('errors.missingToken'));
            return Endpoints.v1.Authentication.requestResetEmail(token, {
                newEmailAddress: newEmail,
                currentlyPassword: password,
            });
        },
        onSuccess: () => {
            notifications.show({
                title: t('toast.codeSentTitle'),
                message: t('toast.codeSentMessage'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            setStage('enter-otp');
        },
        onError: (err: unknown) => {
            notifications.show({
                title: t('toast.errorTitle'),
                message: getErrorMessage(err) ?? t('toast.errorMessage'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const confirmMutation = useMutation({
        mutationFn: () => {
            const token = storage.get<string>('_zugang', '');
            if (!token) throw new Error(t('errors.missingToken'));
            return Endpoints.v1.Authentication.resetEmail(otp, token);
        },
        onSuccess: async () => {
            notifications.show({
                title: t('toast.emailChangedTitle'),
                message: t('toast.emailChangedMessage'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
            dispatch(requestAuthUser());
            setStage('idle');
            setNewEmail('');
            setPassword('');
            setOtp('');
            setSubmitAttempted(false);
            setOtpResetKey((k) => k + 1);
        },
        onError: (err: unknown) => {
            notifications.show({
                title: t('toast.errorTitle'),
                message: getErrorMessage(err) ?? t('toast.errorMessage'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const handleRequest = () => {
        if (!submitAttempted) setSubmitAttempted(true);
        if (Object.keys(requestValidation).length > 0) return;
        requestMutation.mutate();
    };

    const handleConfirm = () => {
        if (otp.length !== 6) return;
        confirmMutation.mutate();
    };

    return (
        <DialogBox
            label={t('email.title')}
            description={t('email.description')}
            classNames="p-4 gap-4"
        >
            <InputField size="md" label={t('email.current')} value={user.email} disabled />
            {stage === 'idle' ? (
                <div className="w-full flex flex-col gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputField
                            size="md"
                            label={t('email.newEmail')}
                            placeholder={t('email.newEmailPlaceholder')}
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            error={submitAttempted ? requestValidation.newEmail : undefined}
                        />
                        <InputField
                            size="md"
                            type="password"
                            passwordReveal
                            label={t('email.currentPassword')}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={submitAttempted ? requestValidation.password : undefined}
                        />
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={handleRequest} isLoading={requestMutation.isPending}>
                            {t('email.requestCode')}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="w-full flex flex-col gap-4">
                    <div>
                        <span className="text-sm block mb-2">{t('email.otp')}</span>
                        <OtpInputField
                            length={6}
                            onComplete={setOtp}
                            onChange={setOtp}
                            resetKey={otpResetKey}
                        />
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={handleConfirm} isLoading={confirmMutation.isPending}>
                            {t('email.submit')}
                        </Button>
                    </div>
                </div>
            )}
        </DialogBox>
    );
}
